import React, { FC, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import axios from 'axios'
import { Button, IconButton, Paper, Theme, Typography } from '@material-ui/core'
import { createStyles, makeStyles } from '@material-ui/styles'
import CloseIcon from '@material-ui/icons/Close'
import useWorkspaceId from 'src/hooks/useWorkspaceId'

import { buildMessagingCmsBannerRequest } from './messaging-cms-banner.helpers'

interface MessagingCmsBanner {
  id: string
  campaignId: string
  title: string
  body: string
  ctaLabel?: string
  ctaUrl?: string
  expiresAt?: string
  createdAt: string
}

const { REACT_APP_PORTAL_API_URL } = process.env

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      display: 'flex',
      flexDirection: 'column'
    },
    banner: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(2),
      padding: theme.spacing(1.25, 3),
      borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
      backgroundColor: '#F8FAFC'
    },
    text: {
      flex: 1,
      minWidth: 0
    },
    title: {
      fontWeight: 600,
      fontSize: 14,
      lineHeight: 1.4
    },
    body: {
      fontSize: 13,
      color: 'rgba(0, 0, 0, 0.72)',
      marginTop: 2
    },
    cta: {
      flexShrink: 0,
      textTransform: 'none'
    },
    closeButton: {
      flexShrink: 0,
      padding: 4
    }
  })
)

function readToken(): string | null {
  try {
    return localStorage.getItem('token')
  } catch {
    return null
  }
}

async function fetchBanners(token: string, workspaceId?: string): Promise<MessagingCmsBanner[]> {
  const { url, ...config } = buildMessagingCmsBannerRequest({
    apiUrl: REACT_APP_PORTAL_API_URL as string,
    token,
    workspaceId
  })
  const response = await axios.get<{ data: MessagingCmsBanner[] }>(url, config)
  return response.data?.data ?? []
}

async function dismissBanner(token: string, id: string): Promise<void> {
  await axios.patch(
    `${REACT_APP_PORTAL_API_URL}/v1/messaging/cms-banners/${id}/dismiss`,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  )
}

async function recordClick(token: string, id: string): Promise<void> {
  await axios.patch(
    `${REACT_APP_PORTAL_API_URL}/v1/messaging/cms-banners/${id}/click`,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  )
}

export const MessagingCmsBanner: FC = () => {
  const classes = useStyles()
  const workspaceId = useWorkspaceId()
  const requestSequenceRef = useRef(0)
  const previousWorkspaceIdRef = useRef(workspaceId)
  const [banners, setBanners] = useState<MessagingCmsBanner[]>([])

  useLayoutEffect(() => {
    if (previousWorkspaceIdRef.current === workspaceId) return

    previousWorkspaceIdRef.current = workspaceId
    requestSequenceRef.current += 1
    setBanners([])
  }, [workspaceId])

  const load = useCallback(() => {
    if (!REACT_APP_PORTAL_API_URL) return

    const requestId = requestSequenceRef.current + 1
    requestSequenceRef.current = requestId
    const token = readToken()
    if (!token) {
      setBanners([])
      return
    }
    fetchBanners(token, workspaceId)
      .then(nextBanners => {
        if (requestSequenceRef.current === requestId) setBanners(nextBanners)
      })
      .catch(() => {
        // Silently ignore — banner channel is non-critical
      })
  }, [workspaceId])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === 'visible') load()
    }
    document.addEventListener('visibilitychange', handler)
    return () => document.removeEventListener('visibilitychange', handler)
  }, [load])

  const handleDismiss = useCallback((id: string) => {
    setBanners(prev => prev.filter(banner => banner.id !== id))
    const token = readToken()
    if (!token) return
    dismissBanner(token, id).catch(() => {
      // If the request fails the banner will reappear on the next fetch; that
      // is fine because the next visibilitychange refetch will surface it
      // again with the same id.
    })
  }, [])

  const handleCtaClick = useCallback((id: string) => {
    // The CTA <a> opens ctaUrl in a new tab, so this tab stays alive long
    // enough for the fire-and-forget click report to complete.
    const token = readToken()
    if (!token) return
    recordClick(token, id).catch(() => {
      // Non-critical: CTA click analytics only.
    })
  }, [])

  if (banners.length === 0) return null

  return (
    <div className={classes.container}>
      {banners.map(banner => (
        <Paper key={banner.id} elevation={0} square className={classes.banner}>
          <div className={classes.text}>
            <Typography className={classes.title}>{banner.title}</Typography>
            <Typography className={classes.body}>{banner.body}</Typography>
          </div>
          {banner.ctaLabel && banner.ctaUrl ? (
            <Button
              className={classes.cta}
              color="primary"
              variant="contained"
              size="small"
              href={banner.ctaUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => handleCtaClick(banner.id)}
            >
              {banner.ctaLabel}
            </Button>
          ) : null}
          <IconButton
            className={classes.closeButton}
            aria-label="Dismiss banner"
            size="small"
            onClick={() => handleDismiss(banner.id)}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Paper>
      ))}
    </div>
  )
}
